import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

import { ExternalQuestion } from '@sm/question-definitions';
import { ExternalValidator, OpenEndedAnswerType } from '@sm/question-widgets/respondent-survey';

import { GetCurrentSurveySessionQuery } from '~lib/generatedGqlTypes';

import { SurveyContext } from './SurveyContext';
import { useSurveyErrorsContext } from './errors';
import transformToSurveyErrors from './errors/transformToSurveyErrors';
import { useValidationManager } from './validation';
import { QuestionAnswers } from './types';
import { deferToAfterFocusChanged } from './validation/delayedValidation';

type CurrentPageContextType = {
  questions?: NonNullable<
    NonNullable<GetCurrentSurveySessionQuery['surveyTakingSession']['respondent']['currentPage']>['questionsQM']
  >['items'];
  answers?: QuestionAnswers;
  totalAnswers: number;
  updateAnswer(this: void, question: ExternalQuestion, answer: OpenEndedAnswerType): void;
  registerValidator(this: void, questionId: string, newValidators: ExternalValidator[]): void;
  unregisterValidator(this: void, questionId: string): void;
  validatePage(this: void): boolean;
};

export const CurrentPageContext = createContext<CurrentPageContextType>({
  questions: undefined,
  answers: undefined,
  totalAnswers: 0,
  updateAnswer: () => {},
  registerValidator: () => {},
  unregisterValidator: () => {},
  validatePage: () => true,
});

type CurrentPageProviderProps = {
  children: React.ReactNode;
};

export function CurrentPageProvider({ children }: CurrentPageProviderProps): React.ReactElement {
  // Local storage -> overrides data from survey.
  // Local storage should be cleared when we hit the next page button.

  const { activePage } = useContext(SurveyContext);
  const { removeErrorsById, removeErrorsByType, setErrorsById, errorGroups } = useSurveyErrorsContext();

  const [questions] = useState(activePage?.questionsQM?.items);

  const { registerValidator, unregisterValidator, validate, runValidators } = useValidationManager();

  const initialAnswers = questions?.reduce(
    (acc, curr) => ({
      ...acc,
      [curr.id]: {
        questionId: curr.id,
        values: [],
        touched: false,
        isDirty: false,
      },
    }),
    {}
  );

  // Hydrate answers
  const [answers, setAnswers] = useState<QuestionAnswers>(initialAnswers || {});

  // context functions
  const updateAnswer = useCallback<CurrentPageContextType['updateAnswer']>(
    (question, answer) => {
      if (!answer.touched) {
        return; // no need for updates if answer was not touched
      }

      const { isValid, errors } = validate(question.id);
      deferToAfterFocusChanged(() => {
        // defer to make done with error-change click work
        if (!isValid) {
          const questionErrors = transformToSurveyErrors(errors);
          setErrorsById(question.id, questionErrors);
        } else {
          removeErrorsById(question.id);
        }
      });

      // Use functional form because the updated value is based on the previous value
      setAnswers(prevAns => {
        return {
          ...prevAns,
          [question.id]: { ...answers[question.id], ...answer },
        };
      }); // rebuild new state object to store answers
    },
    [answers, removeErrorsById, setErrorsById, validate]
  );

  /**
   * the total number of questions that have a value (i.e. answered), on the current page only
   * note: currently only works with single-value questions
   * TODO: refactor to include answered questions on other pages once we get that data from the backend
   *  */
  const totalAnswers = useMemo(
    () =>
      !answers
        ? 0
        : Object.values(answers).reduce((acc: number, answer) => (answer.values?.[0]?.value ? acc + 1 : acc), 0),
    [answers]
  );

  const validatePage = useCallback((): boolean => {
    const { isValid, errors } = runValidators();
    if (!isValid) {
      // temporary while aligning error-shape between Server and SMQ
      const questionErrors = transformToSurveyErrors(errors);
      const questionIds = questionErrors.map(a => a.questionId);
      setErrorsById(questionIds, questionErrors);
    } else {
      removeErrorsByType(errorGroups.validation);
    }
    return isValid;
  }, [setErrorsById, removeErrorsByType, runValidators, errorGroups]);

  return (
    <CurrentPageContext.Provider
      value={{
        questions,
        answers,
        totalAnswers,
        updateAnswer,
        registerValidator,
        unregisterValidator,
        validatePage,
      }}
    >
      <>{children}</>
    </CurrentPageContext.Provider>
  );
}

export default CurrentPageProvider;
